import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Plus, UserPlus, Check, ArrowUpRight, Zap, Copy, RefreshCw, QrCode, Mail, Pencil, Trash2, X, AlertCircle, Download, Upload, Settings as SettingsIcon, Info, Eye, Calculator } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Navbar } from "@/components/Navbar";
import { CommandPalette } from "@/components/CommandPalette";
import { BillDrawer } from "@/components/BillDrawer";
import { AnimatedNumber } from "@/components/AnimatedNumber";
import { GroupSettingsDialog } from "@/components/GroupSettingsDialog";
import { ActionBottomSheet } from "@/components/ActionBottomSheet";
import { api, formatAmount, CATEGORIES, currencySymbol } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useHistoryTab } from "@/hooks/useHistoryTab";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { formatDistanceToNowStrict, format } from "date-fns";
import confetti from "canvas-confetti";

const UNDO_MS = 5000;

export default function GroupDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [group, setGroup] = useState(null);
  const [bills, setBills] = useState([]);
  const [balance, setBalance] = useState(null);
  const [cmdkOpen, setCmdkOpen] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const [settling, setSettling] = useState(null);
  const [recordingFrom, setRecordingFrom] = useState(null); // transaction being recorded as received
  const [recordAmount, setRecordAmount] = useState("");
  const [recordMethod, setRecordMethod] = useState("cash");
  const [recordNotes, setRecordNotes] = useState("");
  const [recording, setRecording] = useState(false);
  const [billDrawerOpen, setBillDrawerOpen] = useState(false);
  const [editingBill, setEditingBill] = useState(null);

  // Pending delete: { bill, timer }
  const [pendingDelete, setPendingDelete] = useState(null);
  const pendingRef = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [debtView, setDebtView] = useState(() => localStorage.getItem("stb_debt_view") || "simple");
  const [actionBill, setActionBill] = useState(null); // bill row tapped
  const [detailsBill, setDetailsBill] = useState(null); // bill being viewed in detail
  const [auditFor, setAuditFor] = useState(null); // user_id whose balance audit is open
  const [gtab, setGtab] = useHistoryTab("bills");
  useEffect(() => { localStorage.setItem("stb_debt_view", debtView); }, [debtView]);

  const inviteUrl = group?.invite_code ? `${window.location.origin}/join/${group.invite_code}` : "";

  // ===== Load =====
  const loadAll = async () => {
    const [g, b, bal] = await Promise.all([
      api.get(`/groups/${id}`),
      api.get(`/bills?group_id=${id}`),
      api.get(`/groups/${id}/balance`),
    ]);
    setGroup(g.data);
    setBills(b.data);
    setBalance(bal.data);
  };
  const refreshBalance = async () => {
    const bal = await api.get(`/groups/${id}/balance`);
    setBalance(bal.data);
  };

  useEffect(() => { loadAll(); }, [id]);

  // ===== Local recompute for optimistic updates =====
  const localBalance = useMemo(() => {
    if (!group) return null;
    const memberIds = group.member_ids;
    const visibleBills = bills.filter((b) => b.id !== pendingDelete?.bill?.id);
    const balMap = Object.fromEntries(memberIds.map((id) => [id, 0]));
    for (const b of visibleBills) {
      balMap[b.paid_by] = (balMap[b.paid_by] || 0) + Number(b.amount);
      for (const s of b.splits) balMap[s.user_id] = (balMap[s.user_id] || 0) - Number(s.amount);
    }
    if (balance?.settlements_raw) {
      // no-op, balance endpoint applies settlements
    }
    // Include settlements from server balance (we approximate by adjusting)
    if (balance) {
      // Compute server balance minus our (already-counted) bill effect; simpler: overlay settlement diffs
      // Use formula: effective_i = balance.balance[i] +/- effect of pending delete
      const serverBal = { ...balance.balance };
      if (pendingDelete?.bill) {
        const b = pendingDelete.bill;
        // Remove this bill's effect from server balance
        serverBal[b.paid_by] = (serverBal[b.paid_by] || 0) - Number(b.amount);
        for (const s of b.splits) {
          serverBal[s.user_id] = (serverBal[s.user_id] || 0) + Number(s.amount);
        }
      }
      return serverBal;
    }
    return balMap;
  }, [group, bills, balance, pendingDelete]);

  const simplifiedTx = useMemo(() => {
    if (!localBalance) return [];
    const creditors = Object.entries(localBalance)
      .filter(([, v]) => v > 0.01)
      .map(([u, v]) => [u, Math.round(v * 100) / 100])
      .sort((a, b) => b[1] - a[1]);
    const debtors = Object.entries(localBalance)
      .filter(([, v]) => v < -0.01)
      .map(([u, v]) => [u, Math.round(-v * 100) / 100])
      .sort((a, b) => b[1] - a[1]);
    const txs = [];
    let i = 0, j = 0;
    while (i < debtors.length && j < creditors.length) {
      const pay = Math.min(debtors[i][1], creditors[j][1]);
      txs.push({ from: debtors[i][0], to: creditors[j][0], amount: Math.round(pay * 100) / 100 });
      debtors[i][1] -= pay;
      creditors[j][1] -= pay;
      if (debtors[i][1] < 0.01) i++;
      if (creditors[j][1] < 0.01) j++;
    }
    return txs;
  }, [localBalance]);

  // Per-pair raw debts: sum every bill's per-person share, net by pair (no minimization).
  const rawDebts = useMemo(() => {
    if (!group) return [];
    const visibleBills = bills.filter((b) => b.id !== pendingDelete?.bill?.id);
    // pair_key = "ower|payer" → amount owed
    const owes = {};
    for (const b of visibleBills) {
      const payer = b.paid_by;
      for (const s of b.splits) {
        if (s.user_id === payer) continue;
        const k = `${s.user_id}|${payer}`;
        owes[k] = (owes[k] || 0) + Number(s.amount);
      }
    }
    // Apply settlements: a payment from X to Y reduces "X owes Y"
    const allSettlements = (balance?.settlements_raw) || balance?.settlements || [];
    for (const s of allSettlements) {
      const k = `${s.from_user}|${s.to_user}`;
      owes[k] = (owes[k] || 0) - Number(s.amount);
    }
    // Net opposing pairs, drop near-zero
    const seen = new Set();
    const rows = [];
    for (const k of Object.keys(owes)) {
      if (seen.has(k)) continue;
      const [a, b] = k.split("|");
      const rev = `${b}|${a}`;
      const v = (owes[k] || 0) - (owes[rev] || 0);
      seen.add(k); seen.add(rev);
      if (v > 0.01) rows.push({ from: a, to: b, amount: Math.round(v * 100) / 100 });
      else if (v < -0.01) rows.push({ from: b, to: a, amount: Math.round(-v * 100) / 100 });
    }
    rows.sort((x, y) => y.amount - x.amount);
    return rows;
  }, [group, bills, balance, pendingDelete]);

  const debtsToShow = debtView === "smart" ? simplifiedTx : rawDebts;

  // ===== Invite =====
  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post(`/groups/${id}/invites`, { email: inviteEmail });
      if (data.status === "pending") {
        toast.success(`Invite sent to ${inviteEmail} — they'll see it in their Groups inbox`);
      } else {
        toast.success(`Invited ${inviteEmail}`);
      }
      setInviteOpen(false);
      setInviteEmail("");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  const copyInvite = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Invite link copied");
    } catch {
      toast.error("Couldn't copy — select and copy manually");
    }
  };

  const regenerateCode = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/groups/${id}/invite/regenerate`);
      setGroup((g) => ({ ...g, invite_code: data.invite_code }));
      toast.success("New invite link generated");
    } catch {
      toast.error("Failed to regenerate");
    } finally {
      setRegenerating(false);
    }
  };

  // ===== Settlement =====
  const confirmSettle = async () => {
    if (!settling) return;
    try {
      await api.post("/settlements", {
        group_id: id,
        to_user: settling.to,
        amount: settling.amount,
        currency: group.currency,
      });
      confetti({ particleCount: 120, spread: 70, origin: { y: 0.6 }, colors: ["#00FF66", "#FFFFFF", "#00D4FF"] });
      toast.success("Settled ✓");
      setSettling(null);
      refreshBalance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  // ===== Record payment received (offline/cash from someone who owes me) =====
  const openRecord = (tx) => {
    setRecordingFrom(tx);
    setRecordAmount(String(tx.amount));
    setRecordMethod("cash");
    setRecordNotes("");
  };

  const confirmRecord = async () => {
    if (!recordingFrom) return;
    const amt = Number(recordAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setRecording(true);
    try {
      await api.post("/settlements/record-received", {
        group_id: id,
        from_user: recordingFrom.from,
        amount: amt,
        currency: group.currency,
        method: recordMethod,
        notes: recordNotes || null,
      });
      confetti({ particleCount: 80, spread: 55, origin: { y: 0.7 }, colors: ["#00FF66", "#FFFFFF"] });
      toast.success(`Recorded ${formatAmount(amt, group.currency)} received`);
      setRecordingFrom(null);
      refreshBalance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setRecording(false);
    }
  };

  // ===== Bill Save =====
  const handleBillSaved = (savedBill) => {
    setBills((prev) => {
      const idx = prev.findIndex((b) => b.id === savedBill.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = savedBill;
        return next;
      }
      return [savedBill, ...prev];
    });
    setEditingBill(null);
    refreshBalance();
  };

  const openEdit = (bill) => {
    setEditingBill(bill);
    setBillDrawerOpen(true);
  };

  const openAdd = () => {
    setEditingBill(null);
    setBillDrawerOpen(true);
  };

  // ===== Delete with Undo =====
  const beginDelete = (bill) => {
    if (pendingRef.current) {
      // Finalize any existing pending delete immediately
      const prev = pendingRef.current;
      clearTimeout(prev.timer);
      if (prev.toastId) toast.dismiss(prev.toastId);
      api.delete(`/bills/${prev.bill.id}`).catch(() => {});
    }

    const timer = setTimeout(async () => {
      try {
        await api.delete(`/bills/${bill.id}`);
      } catch (err) {
        toast.error("Failed to delete");
        setBills((prev) => [bill, ...prev]);
      }
      setPendingDelete(null);
      pendingRef.current = null;
      refreshBalance();
    }, UNDO_MS);

    const toastId = toast(`Bill "${bill.title}" deleted`, {
      description: "Undo within 5s",
      duration: UNDO_MS,
      action: {
        label: "Undo",
        onClick: () => undoDelete(bill.id),
      },
      className: "font-sans",
    });

    const entry = { bill, timer, toastId };
    pendingRef.current = entry;
    setPendingDelete(entry);
    setBills((prev) => prev.filter((b) => b.id !== bill.id));
  };

  const undoDelete = (billId) => {
    const entry = pendingRef.current;
    if (!entry || entry.bill.id !== billId) return;
    clearTimeout(entry.timer);
    setBills((prev) => [entry.bill, ...prev].sort((a, b) => (a.created_at < b.created_at ? 1 : -1)));
    pendingRef.current = null;
    setPendingDelete(null);
    toast.success("Bill restored");
  };

  useEffect(() => () => {
    if (pendingRef.current?.timer) clearTimeout(pendingRef.current.timer);
  }, []);

  if (!group || !balance) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar onOpenCmdK={() => setCmdkOpen(true)} />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 text-muted-foreground">Loading…</div>
      </div>
    );
  }

  const memberById = (uid) => balance.members.find((m) => m.id === uid) || group.members.find((m) => m.id === uid);
  const memberName = (uid) => memberById(uid)?.name || "Unknown";
  const memberEmoji = (uid) => memberById(uid)?.avatar_emoji || "🙂";

  const visibleBills = bills;
  const totalOwedToMe = simplifiedTx.filter((t) => t.to === user.id).reduce((s, t) => s + t.amount, 0);
  const totalIOwe = simplifiedTx.filter((t) => t.from === user.id).reduce((s, t) => s + t.amount, 0);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar onOpenCmdK={() => setCmdkOpen(true)} />
      <CommandPalette open={cmdkOpen} setOpen={setCmdkOpen} />

      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div className="flex items-start gap-5 min-w-0">
            <div className="h-16 w-16 rounded-2xl bg-foreground/5 border border-border flex items-center justify-center text-3xl shrink-0">{group.emoji}</div>
            <div className="min-w-0">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1.5">
                / {group.group_type} · {group.currency}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter truncate" data-testid="group-name">{group.name}</h1>
              <div className="mt-3 flex -space-x-2">
                {group.members.map((m) => (
                  <div key={m.id} className="h-8 w-8 rounded-full bg-foreground/10 border-2 border-background flex items-center justify-center text-sm" title={m.name}>
                    {m.avatar_emoji || "🙂"}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-border bg-foreground/[0.02] hover:bg-foreground/[0.05] h-10" data-testid="group-invite-btn">
                  <UserPlus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Invite
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">Invite to {group.name}</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="link" className="mt-2">
                  <TabsList className="bg-foreground/5 border border-border w-full grid grid-cols-2" data-testid="invite-tabs">
                    <TabsTrigger value="link" className="data-[state=active]:bg-background" data-testid="invite-tab-link">
                      <QrCode className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Link & QR
                    </TabsTrigger>
                    <TabsTrigger value="email" className="data-[state=active]:bg-background" data-testid="invite-tab-email">
                      <Mail className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> By email
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="link" className="space-y-4 mt-4">
                    <div className="flex justify-center p-5 rounded-xl bg-white border border-border">
                      {group.invite_code ? (
                        <QRCodeSVG value={inviteUrl} size={180} level="M" bgColor="#FFFFFF" fgColor="#000000" data-testid="invite-qr-svg" />
                      ) : (<div className="h-[180px] w-[180px] bg-foreground/5 animate-pulse rounded" />)}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Shareable link</Label>
                      <div className="flex gap-2">
                        <Input readOnly value={inviteUrl} className="font-mono text-xs bg-muted border-border" onFocus={(e) => e.target.select()} data-testid="invite-link-input" />
                        <Button type="button" variant="outline" size="icon" className="border-border shrink-0" onClick={copyInvite} data-testid="invite-copy-btn" aria-label="Copy link">
                          <Copy className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-border">
                      <div className="text-xs text-muted-foreground">
                        Code: <span className="font-mono text-foreground">{group.invite_code || "…"}</span>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={regenerateCode} disabled={regenerating} className="text-xs" data-testid="invite-regen-btn">
                        <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${regenerating ? "animate-spin" : ""}`} strokeWidth={1.5} /> Regenerate
                      </Button>
                    </div>
                  </TabsContent>
                  <TabsContent value="email" className="space-y-4 mt-4">
                    <form onSubmit={invite} className="space-y-4" data-testid="invite-form">
                      <div>
                        <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
                        <Input type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="mt-1.5 bg-muted border-border" placeholder="friend@example.com" data-testid="invite-email-input" />
                        <p className="text-[11px] text-muted-foreground mt-2">They'll get a pending invite in their Groups inbox — they need an account first.</p>
                      </div>
                      <DialogFooter>
                        <Button type="submit" disabled={inviting} className="brand-bg text-black hover:opacity-90 w-full" data-testid="invite-submit">
                          {inviting ? "Sending…" : "Send invite"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>

            <Button onClick={openAdd} className="brand-bg text-black hover:opacity-90 h-11" data-testid="group-new-bill-btn">
              <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Add bill
            </Button>
            <Button
              variant="outline"
              onClick={() => setSettingsOpen(true)}
              className="border-border bg-foreground/[0.02] hover:bg-foreground/[0.05] h-11 w-11 p-0 shrink-0"
              data-testid="group-settings-btn"
              aria-label="Group settings"
            >
              <SettingsIcon className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Bills + Payment History tabs */}
          <section className="lg:col-span-7" data-testid="group-bills-panel">
            <Tabs value={gtab} onValueChange={setGtab}>
              <TabsList className="bg-card border border-border" data-testid="group-tabs">
                <TabsTrigger value="bills" className="data-[state=active]:bg-foreground/10" data-testid="tab-bills">
                  {t("groups.bills_tab")}
                </TabsTrigger>
                <TabsTrigger value="history" className="data-[state=active]:bg-foreground/10" data-testid="tab-history">
                  {t("groups.payment_history_tab")}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="bills">
                <div className="rounded-2xl border border-border bg-card/40 p-6 mt-4">
                  <div className="flex items-center justify-between mb-5">
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{t("groups.bills_tab")}</div>
                    <div className="text-xs font-mono text-muted-foreground" data-testid="bills-count-badge">
                      <AnimatedNumber value={visibleBills.length} duration={0.4} /> total
                    </div>
                  </div>
                  {visibleBills.length === 0 ? (
                    <div className="py-14 text-center text-sm text-muted-foreground">
                      <div>No bills yet.</div>
                      <button onClick={openAdd} className="brand-text hover:underline" data-testid="empty-add-bill">
                        Add the first one →
                      </button>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      <AnimatePresence initial={false}>
                        {visibleBills.map((b) => (
                          <BillRow
                            key={b.id} bill={b}
                            memberName={memberName}
                            onEdit={() => openEdit(b)}
                            onDelete={() => beginDelete(b)}
                            onTap={() => setActionBill(b)}
                            currency={group.currency}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="history">
                <PaymentHistoryPanel
                  groupId={id}
                  currency={group.currency}
                  memberName={memberName}
                  memberEmoji={memberEmoji}
                  t={t}
                />
              </TabsContent>
            </Tabs>
          </section>

          {/* Right panels */}
          <section className="lg:col-span-5 space-y-5">
            <div className="rounded-2xl border border-border bg-card/40 p-6 tracing-beam" data-testid="simplified-panel">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <div className="h-7 w-7 rounded-md brand-bg text-black flex items-center justify-center">
                  <Zap className="h-3.5 w-3.5" strokeWidth={2} />
                </div>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                  {debtView === "smart" ? t("groups.smart_settlements") : t("groups.who_owes_whom")}
                </div>
                {debtView === "smart" && (
                  <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded brand-bg text-black">{t("groups.smart")}</span>
                )}
              </div>
              {/* View toggle */}
              <div className="mb-5 flex items-center justify-between gap-2 rounded-lg border border-border bg-foreground/[0.02] p-1" data-testid="debt-view-toggle">
                <button
                  onClick={() => setDebtView("simple")}
                  className={`flex-1 h-8 rounded-md text-xs font-mono uppercase tracking-wider transition ${debtView === "simple" ? "bg-foreground/10 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="debt-view-simple"
                >
                  {t("groups.simple_view")}
                </button>
                <button
                  onClick={() => setDebtView("smart")}
                  className={`flex-1 h-8 rounded-md text-xs font-mono uppercase tracking-wider transition ${debtView === "smart" ? "brand-bg text-black" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="debt-view-smart"
                >
                  {t("groups.smart_view")}
                </button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="h-8 w-8 rounded-md text-muted-foreground hover:text-foreground transition shrink-0" data-testid="debt-view-info" aria-label="What's the difference?">
                        <Info className="h-3.5 w-3.5 mx-auto" strokeWidth={1.5} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-[240px] text-xs">
                      {t("groups.smart_tooltip")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {debtsToShow.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center">{t("groups.all_settled")}</div>
              ) : (
                <div className="space-y-2">
                  <AnimatePresence initial={false}>
                    {debtsToShow.map((tx, idx) => {
                      const isMineOut = tx.from === user.id;
                      const isMineIn = tx.to === user.id;
                      return (
                        <motion.div
                          key={`${tx.from}-${tx.to}-${idx}`}
                          layout
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.3 }}
                          className="rounded-lg border border-border bg-foreground/[0.02] p-3 flex items-center justify-between gap-2 flex-wrap"
                          data-testid={`settlement-row-${idx}`}
                        >
                          <div className="flex items-center gap-2 text-sm min-w-0 basis-full sm:basis-auto sm:flex-1 order-1">
                            <span className="shrink-0 h-7 w-7 rounded-full bg-foreground/5 flex items-center justify-center text-xs">{memberEmoji(tx.from)}</span>
                            <span className="truncate">{memberName(tx.from)}</span>
                            <ArrowUpRight className="h-3 w-3 text-muted-foreground shrink-0" strokeWidth={1.5} />
                            <span className="shrink-0 h-7 w-7 rounded-full bg-foreground/5 flex items-center justify-center text-xs">{memberEmoji(tx.to)}</span>
                            <span className="truncate">{memberName(tx.to)}</span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0 order-2 ml-auto sm:ml-0">
                            <AnimatedNumber value={tx.amount} currency={group.currency} className="text-sm" />
                            {isMineOut && (
                              <Button size="sm" onClick={() => setSettling(tx)} className="brand-bg text-black hover:opacity-90 h-8 px-3" data-testid={`settle-btn-${idx}`}>
                                <Check className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> {t("common.settle")}
                              </Button>
                            )}
                            {isMineIn && (
                              <Button
                                size="sm"
                                onClick={() => openRecord(tx)}
                                className="brand-bg text-black hover:opacity-90 h-8 px-3"
                                data-testid={`record-received-btn-${idx}`}
                              >
                                <Check className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> {t("common.record")}
                              </Button>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-border bg-card/40 p-6" data-testid="balances-panel">
              <div className="flex items-center justify-between mb-5">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Balances</div>
                <button
                  onClick={() => setAuditFor(user.id)}
                  className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:brand-text transition flex items-center gap-1"
                  data-testid="audit-trail-link"
                >
                  <Calculator className="h-3 w-3" strokeWidth={1.5} /> How is this calculated?
                </button>
              </div>
              <div className="space-y-2">
                {Object.entries(localBalance || {}).map(([uid, v]) => (
                  <button
                    key={uid}
                    onClick={() => setAuditFor(uid)}
                    className="w-full flex items-center justify-between text-sm py-1.5 px-2 -mx-2 rounded-md hover:bg-foreground/[0.03] transition"
                    data-testid={`balance-row-${uid}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="h-7 w-7 rounded-full bg-foreground/5 flex items-center justify-center text-xs">{memberEmoji(uid)}</span>
                      <span className="truncate">{memberName(uid)}</span>
                    </div>
                    <AnimatedNumber value={Math.abs(v) < 0.005 ? 0 : v} currency={group.currency} withSign className={Math.abs(v) < 0.005 ? "text-muted-foreground" : (v >= 0 ? "brand-text" : "text-red-400")} />
                  </button>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Bill drawer */}
      <BillDrawer
        open={billDrawerOpen}
        onOpenChange={(o) => { setBillDrawerOpen(o); if (!o) setEditingBill(null); }}
        group={group}
        bill={editingBill}
        onSaved={handleBillSaved}
      />

      {/* Group settings */}
      <GroupSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        group={group}
        currentUserId={user.id}
        onUpdated={(updated) => { if (updated) setGroup((g) => ({ ...g, ...updated })); loadAll(); }}
      />

      {/* Mobile tap-to-action sheet on bills */}
      <ActionBottomSheet
        open={!!actionBill}
        onOpenChange={(o) => !o && setActionBill(null)}
        title={actionBill?.title}
        subtitle={actionBill ? formatAmount(actionBill.amount, actionBill.currency || group.currency) : ""}
        actions={[
          { icon: <Pencil className="h-4 w-4" strokeWidth={1.5} />, label: "Edit split", onClick: () => openEdit(actionBill), testId: "bs-edit-bill" },
          { icon: <Eye className="h-4 w-4" strokeWidth={1.5} />, label: "View details", onClick: () => setDetailsBill(actionBill), testId: "bs-view-bill" },
          { icon: <Trash2 className="h-4 w-4" strokeWidth={1.5} />, label: "Delete entry", danger: true, onClick: () => beginDelete(actionBill), testId: "bs-delete-bill" },
        ]}
      />

      {/* Bill details dialog */}
      <Dialog open={!!detailsBill} onOpenChange={(o) => !o && setDetailsBill(null)}>
        <DialogContent className="bg-card border-border max-w-md max-h-[85vh] overflow-y-auto" data-testid="bill-details-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{detailsBill?.title}</DialogTitle>
          </DialogHeader>
          {detailsBill && (
            <div className="space-y-4">
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-3xl">{formatAmount(detailsBill.amount, detailsBill.currency || group.currency)}</span>
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">· {detailsBill.split_type} split</span>
              </div>
              <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                {format(new Date(detailsBill.created_at), "MMM d, yyyy · h:mm a")} · paid by {memberName(detailsBill.paid_by)}
              </div>
              {detailsBill.description && <p className="text-sm text-muted-foreground border-l-2 border-border pl-3">{detailsBill.description}</p>}
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Split breakdown</div>
                <div className="divide-y divide-border rounded-lg border border-border bg-foreground/[0.02]">
                  {detailsBill.splits.map((s) => (
                    <div key={s.user_id} className="flex items-center justify-between py-2.5 px-3" data-testid={`split-row-${s.user_id}`}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="h-8 w-8 rounded-full bg-foreground/5 flex items-center justify-center text-sm">{memberEmoji(s.user_id)}</span>
                        <span className="text-sm truncate">{memberName(s.user_id)}{s.user_id === detailsBill.paid_by && <span className="text-[10px] font-mono brand-text ml-1.5">PAID</span>}</span>
                      </div>
                      <span className="font-mono tabular-nums text-sm">{formatAmount(s.amount, detailsBill.currency || group.currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Balance audit trail dialog */}
      <Dialog open={!!auditFor} onOpenChange={(o) => !o && setAuditFor(null)}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">How {auditFor === user.id ? "your" : `${memberName(auditFor)}'s`} balance is calculated</DialogTitle>
          </DialogHeader>
          {auditFor && (() => {
            const uid = auditFor;
            const visibleBills = bills.filter((b) => b.id !== pendingDelete?.bill?.id);
            const paidBills = visibleBills.filter((b) => b.paid_by === uid);
            const sharesForUser = visibleBills
              .map((b) => ({ bill: b, split: b.splits.find((s) => s.user_id === uid) }))
              .filter((x) => x.split);
            const paidTotal = paidBills.reduce((s, b) => s + Number(b.amount), 0);
            const shareTotal = sharesForUser.reduce((s, x) => s + Number(x.split.amount), 0);
            const settlements = (balance?.settlements_raw) || balance?.settlements || [];
            const received = settlements.filter((s) => s.to_user === uid);
            const sent = settlements.filter((s) => s.from_user === uid);
            const receivedTotal = received.reduce((s, r) => s + Number(r.amount), 0);
            const sentTotal = sent.reduce((s, r) => s + Number(r.amount), 0);
            const net = paidTotal - shareTotal + receivedTotal - sentTotal;
            const fmt = (n) => formatAmount(n, group.currency);
            const sign = (n) => n > 0.005 ? "+" : n < -0.005 ? "-" : "";
            const fdate = (iso) => format(new Date(iso), "MMM d");
            return (
              <div className="space-y-5" data-testid="audit-breakdown">
                <div className="rounded-xl border border-border bg-foreground/[0.03] p-4">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Net balance</div>
                  <div className={`font-mono tabular-nums text-3xl mt-1 ${net > 0.005 ? "brand-text" : net < -0.005 ? "text-red-400" : ""}`}>
                    {sign(net)}{fmt(Math.abs(net))}
                  </div>
                </div>

                <Step index={1} label={`Bills ${auditFor === user.id ? "you" : "they"} paid for the group`} total={`+${fmt(paidTotal)}`}>
                  {paidBills.length === 0 ? <EmptyLine text="No bills paid yet" /> : paidBills.map((b) => (
                    <AuditRow key={b.id} left={`${b.title} · ${fdate(b.date || b.created_at)}`} right={`+${fmt(b.amount)}`} testId={`audit-paid-${b.id}`} />
                  ))}
                </Step>

                <Step index={2} label={`${auditFor === user.id ? "Your" : "Their"} share of all bills`} total={`-${fmt(shareTotal)}`} tone="red">
                  {sharesForUser.length === 0 ? <EmptyLine text="Not part of any bills" /> : sharesForUser.map(({ bill, split }) => (
                    <AuditRow key={bill.id} left={`${bill.title} · ${fdate(bill.date || bill.created_at)}`} right={`-${fmt(split.amount)}`} testId={`audit-share-${bill.id}`} />
                  ))}
                </Step>

                <Step index={3} label="Payments received" total={`+${fmt(receivedTotal)}`}>
                  {received.length === 0 ? <EmptyLine text="No payments received" /> : received.map((s, i) => (
                    <AuditRow key={s.id || i} left={`From ${memberName(s.from_user)} · ${fdate(s.created_at || s.date)}`} right={`+${fmt(s.amount)}`} testId={`audit-recv-${i}`} />
                  ))}
                </Step>

                <Step index={4} label="Payments made" total={`-${fmt(sentTotal)}`} tone="red">
                  {sent.length === 0 ? <EmptyLine text="No payments made" /> : sent.map((s, i) => (
                    <AuditRow key={s.id || i} left={`To ${memberName(s.to_user)} · ${fdate(s.created_at || s.date)}`} right={`-${fmt(s.amount)}`} testId={`audit-sent-${i}`} />
                  ))}
                </Step>

                <div className="rounded-xl border border-border bg-foreground/[0.03] p-4 font-mono text-xs space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Formula</div>
                  <div>+{fmt(paidTotal)} <span className="text-muted-foreground">paid</span></div>
                  <div>−{fmt(shareTotal)} <span className="text-muted-foreground">share</span></div>
                  <div>+{fmt(receivedTotal)} <span className="text-muted-foreground">received</span></div>
                  <div>−{fmt(sentTotal)} <span className="text-muted-foreground">sent</span></div>
                  <div className="border-t border-border pt-1.5 mt-1.5 flex items-center justify-between">
                    <span className="text-muted-foreground">Net</span>
                    <span className={`text-base ${net > 0.005 ? "brand-text" : net < -0.005 ? "text-red-400" : ""}`}>{sign(net)}{fmt(Math.abs(net))}</span>
                  </div>
                </div>

                <div className="text-[11px] text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Why Smart Settlements differ.</strong> Smart view minimises the number of payments across the group — the total money exchanged is identical, only fewer transfers. Example: instead of A→B €20, A→C €15 and C→B €10, Smart shows A→B €30 · same result, fewer transfers.
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Settle confirmation */}
      <Dialog open={!!settling} onOpenChange={(o) => !o && setSettling(null)}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Confirm settlement</DialogTitle>
          </DialogHeader>
          {settling && (
            <div className="py-3 space-y-4">
              <div className="rounded-lg border brand-border bg-[var(--brand)]/[0.08] p-4 text-center">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Paying</div>
                <div className="font-mono text-3xl brand-text">{formatAmount(settling.amount, group.currency)}</div>
                <div className="text-sm text-muted-foreground mt-1">to {memberName(settling.to)}</div>
              </div>
              <Button onClick={confirmSettle} className="w-full brand-bg text-black hover:opacity-90" data-testid="confirm-settle-btn">
                Mark as paid
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Record payment received (from someone who owes me) */}
      <Dialog open={!!recordingFrom} onOpenChange={(o) => !o && setRecordingFrom(null)}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="record-received-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Record payment received</DialogTitle>
          </DialogHeader>
          {recordingFrom && (
            <div className="py-2 space-y-4">
              <div className="rounded-lg border brand-border bg-[var(--brand)]/[0.06] p-4 flex items-center gap-3">
                <span className="h-10 w-10 rounded-full bg-foreground/10 flex items-center justify-center text-lg">{memberEmoji(recordingFrom.from)}</span>
                <div className="min-w-0">
                  <div className="text-sm text-muted-foreground">Paid by you by</div>
                  <div className="font-medium truncate">{memberName(recordingFrom.from)}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">Owes</div>
                  <div className="font-mono text-sm">{formatAmount(recordingFrom.amount, group.currency)}</div>
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">How much did you receive?</Label>
                <div className="mt-1.5 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-muted-foreground">{currencySymbol(group.currency)}</span>
                  <Input
                    type="number" step="0.01" value={recordAmount}
                    onChange={(e) => setRecordAmount(e.target.value)}
                    className="pl-8 bg-muted border-border h-11 font-mono"
                    autoFocus
                    data-testid="record-amount-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Method</Label>
                <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                  {["cash", "upi", "bank", "other"].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setRecordMethod(m)}
                      className={`h-9 rounded-md border text-xs font-mono uppercase transition ${recordMethod === m ? "brand-border bg-[var(--brand)]/10 brand-text" : "border-border text-muted-foreground hover:text-foreground"}`}
                      data-testid={`record-method-${m}`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Notes (optional)</Label>
                <Input value={recordNotes} onChange={(e) => setRecordNotes(e.target.value)} className="mt-1.5 bg-muted border-border" placeholder="Paid in cash at dinner" data-testid="record-notes-input" />
              </div>
              <Button onClick={confirmRecord} disabled={recording} className="w-full brand-bg text-black hover:opacity-90 h-11" data-testid="record-confirm-btn">
                {recording ? "Recording…" : `Record ${formatAmount(Number(recordAmount || 0), group.currency)}`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const BillRow = ({ bill, memberName, onEdit, onDelete, onTap, currency }) => {
  const cat = CATEGORIES.find((c) => c.value === bill.category);
  const [dragX, setDragX] = useState(0);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -60, height: 0, marginTop: 0, marginBottom: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="group relative overflow-hidden"
      data-testid={`bill-row-${bill.id}`}
    >
      {/* Swipe action layer (mobile) */}
      <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2 pointer-events-none">
        <div
          className={`transition-opacity ${dragX < -40 ? "opacity-100" : "opacity-0"} flex items-center gap-1`}
          style={{ pointerEvents: dragX < -40 ? "auto" : "none" }}
        >
          <button
            onClick={onEdit}
            className="h-11 w-11 rounded-md bg-foreground/10 text-foreground flex items-center justify-center"
            data-testid={`swipe-edit-${bill.id}`}
            aria-label="Edit bill"
          >
            <Pencil className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <button
            onClick={onDelete}
            className="h-11 w-11 rounded-md bg-red-500/15 text-red-400 flex items-center justify-center"
            data-testid={`swipe-delete-${bill.id}`}
            aria-label="Delete bill"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <motion.div
        drag="x"
        dragConstraints={{ left: -120, right: 0 }}
        dragElastic={0.15}
        onDrag={(_, info) => setDragX(info.offset.x)}
        onDragEnd={(_, info) => {
          if (info.offset.x < -80) setDragX(-110);
          else setDragX(0);
        }}
        animate={{ x: dragX }}
        transition={{ type: "spring", stiffness: 400, damping: 40 }}
        className="flex items-center justify-between py-3 bg-background relative cursor-pointer"
        onClick={(e) => {
          // ignore clicks initiated as drags
          if (Math.abs(dragX) > 4) return;
          // only invoke tap handler on mobile (md and below); desktop has hover actions
          if (window.matchMedia("(max-width: 767px)").matches && onTap) {
            e.stopPropagation();
            onTap();
          }
        }}
      >
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <span className="h-10 w-10 rounded-lg bg-foreground/5 flex items-center justify-center text-lg shrink-0">{cat?.emoji || "💸"}</span>
          <div className="min-w-0">
            <div className="font-medium truncate">{bill.title}</div>
            <div className="text-xs text-muted-foreground font-mono uppercase tracking-wider truncate">
              {memberName(bill.paid_by)} paid · {bill.split_type} split
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 amount-col">
          <div className="text-right">
            <div className="font-mono">{formatAmount(bill.amount, bill.currency || currency)}</div>
            <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-wider">
              {format(new Date(bill.date || bill.created_at), "MMM d · h:mm a")}
            </div>
            <div className="text-[10px] font-mono text-muted-foreground/70 tracking-wider">
              {formatDistanceToNowStrict(new Date(bill.date || bill.created_at), { addSuffix: true })}
            </div>
          </div>
          {/* Desktop hover actions */}
          <div className="hidden sm:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={onEdit}
              className="h-8 w-8 rounded-md hover:bg-foreground/10 flex items-center justify-center"
              data-testid={`hover-edit-${bill.id}`}
              aria-label="Edit bill"
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
            <button
              onClick={onDelete}
              className="h-8 w-8 rounded-md hover:bg-red-500/15 hover:text-red-400 flex items-center justify-center"
              data-testid={`hover-delete-${bill.id}`}
              aria-label="Delete bill"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};


const PaymentHistoryPanel = ({ groupId, currency, memberName, memberEmoji, t }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disputing, setDisputing] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/settlements?group_id=${groupId}`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [groupId]);

  const submitDispute = async () => {
    try {
      await api.post(`/settlements/${disputing.id}/dispute`, { reason: reason || "Not acknowledged" });
      toast.success("Dispute filed");
      setDisputing(null); setReason("");
      load();
    } catch { toast.error("Failed"); }
  };

  const exportCsv = () => {
    const header = ["date", "from", "to", "amount", "currency", "method", "disputed"];
    const rows = items.map((i) => [i.created_at, memberName(i.from_user), memberName(i.to_user), i.amount, i.currency, i.method || "", i.disputed ? "true" : "false"]);
    const csv = [header.join(","), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payment-history-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-2xl border border-border bg-card/40 p-6 mt-4" data-testid="payment-history-panel">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{t("groups.payment_history_tab")}</div>
        {items.length > 0 && (
          <Button onClick={exportCsv} variant="outline" size="sm" className="border-border h-8 text-xs" data-testid="history-export-csv">
            <Download className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> {t("groups.download_csv")}
          </Button>
        )}
      </div>
      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className="py-14 text-center text-sm text-muted-foreground" data-testid="history-empty">{t("groups.no_payments")}</div>
      ) : (
        <div className="divide-y divide-border">
          {items.map((i) => (
            <div key={i.id} className="py-3 flex items-center justify-between" data-testid={`history-row-${i.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex items-center gap-1 shrink-0">
                  <span className="h-7 w-7 rounded-full bg-foreground/5 flex items-center justify-center text-xs">{memberEmoji(i.from_user)}</span>
                  <span className="text-muted-foreground text-xs">→</span>
                  <span className="h-7 w-7 rounded-full bg-foreground/5 flex items-center justify-center text-xs">{memberEmoji(i.to_user)}</span>
                </div>
                <div className="min-w-0">
                  <div className="text-sm truncate">{memberName(i.from_user)} → {memberName(i.to_user)}</div>
                  <div className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
                    {formatDistanceToNowStrict(new Date(i.created_at), { addSuffix: true })}{i.method ? ` · ${i.method}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {i.disputed ? (
                  <span className="text-[10px] font-mono uppercase text-red-400 flex items-center gap-1" data-testid={`history-disputed-${i.id}`}>
                    <AlertCircle className="h-3 w-3" strokeWidth={1.5} /> {t("groups.disputed")}
                  </span>
                ) : (
                  <button
                    onClick={() => setDisputing(i)}
                    className="text-[10px] font-mono uppercase text-muted-foreground hover:text-red-400 transition"
                    data-testid={`history-dispute-btn-${i.id}`}
                  >
                    {t("groups.dispute")}
                  </button>
                )}
                <span className="font-mono tabular-nums">{formatAmount(i.amount, i.currency || currency)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!disputing} onOpenChange={(o) => !o && setDisputing(null)}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{t("groups.dispute")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Reason</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} className="bg-muted border-border" placeholder="I didn't receive this" data-testid="dispute-reason-input" />
          </div>
          <DialogFooter>
            <Button onClick={submitDispute} className="brand-bg text-black w-full" data-testid="dispute-submit-btn">{t("groups.mark_disputed")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


const Row = ({ label, v, accent }) => (
  <div className={`flex items-center justify-between py-1 ${accent ? "text-foreground" : "text-muted-foreground"}`}>
    <span className="text-xs uppercase tracking-wider">{label}</span>
    <span className={`tabular-nums ${accent ? "brand-text text-base" : ""}`}>{v}</span>
  </div>
);

const Step = ({ index, label, total, tone, children }) => (
  <section>
    <div className="flex items-center justify-between gap-3 mb-2">
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
        <span className="h-5 w-5 rounded-full bg-foreground/10 flex items-center justify-center text-[10px]">{index}</span>
        {label}
      </div>
      <div className={`text-xs font-mono tabular-nums ${tone === "red" ? "text-red-400" : "brand-text"}`}>{total}</div>
    </div>
    <div className="rounded-lg border border-border bg-foreground/[0.02] divide-y divide-border">{children}</div>
  </section>
);

const AuditRow = ({ left, right, testId }) => (
  <div className="flex items-center justify-between gap-3 px-3 py-2 text-xs font-mono" data-testid={testId}>
    <span className="truncate">{left}</span>
    <span className="tabular-nums shrink-0">{right}</span>
  </div>
);

const EmptyLine = ({ text }) => (
  <div className="px-3 py-2 text-xs text-muted-foreground/70 italic">{text}</div>
);
